import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
interface PrivacyPolicyProps {
  onBack: () => void;
}
export function PrivacyPolicy({
  onBack
}: PrivacyPolicyProps) {
  const {
    language
  } = useLanguage();
  const content = {
    en: {
      title: 'Privacy Policy',
      lastUpdated: 'Last Updated: January 2025',
      backToHome: 'Back to Home',
      sections: [{
        title: '1. Information We Collect',
        content: `We collect information you provide directly to us, such as when you fill out a contact form, schedule a consultation, or communicate with us. This may include:
          
• Personal identification information (name, email address, phone number)
• Fitness and health-related information you choose to share
• Communication preferences
• Any other information you choose to provide`
      }, {
        title: '2. How We Use Your Information',
        content: `We use the information we collect to:

• Respond to your inquiries and provide customer service
• Schedule and manage training sessions
• Send you updates about our services (with your consent)
• Improve our services and develop new offerings
• Comply with legal obligations`
      }, {
        title: '3. Information Sharing',
        content: `We do not sell, trade, or otherwise transfer your personal information to third parties without your consent, except:

• To trusted service providers who assist us in operating our website and conducting our business
• When required by law or to protect our rights
• In connection with a business transfer or merger`
      }, {
        title: '4. Data Security',
        content: `We implement appropriate security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the Internet is 100% secure.`
      }, {
        title: '5. Your Rights',
        content: `You have the right to:

• Access the personal information we hold about you
• Request correction of inaccurate information
• Request deletion of your personal information
• Opt-out of marketing communications
• Withdraw consent at any time`
      }, {
        title: '6. Cookies',
        content: `Our website may use cookies to enhance your browsing experience. You can choose to disable cookies through your browser settings, though this may affect some functionality of our website.`
      }, {
        title: '7. Contact Us',
        content: `If you have any questions about this Privacy Policy, please contact us at:

Email: Example@email.com
Phone: (555) 123-4567
Address: Iron District Gym, 123 Fitness Blvd, Los Angeles, CA`
      }]
    },
    pl: {
      title: 'Polityka Prywatności',
      lastUpdated: 'Ostatnia aktualizacja: Styczeń 2025',
      backToHome: 'Powrót do strony głównej',
      sections: [{
        title: '1. Informacje, które zbieramy',
        content: `Zbieramy informacje, które przekazujesz nam bezpośrednio, na przykład podczas wypełniania formularza kontaktowego, umawiania konsultacji lub komunikacji z nami. Mogą to być:
          
• Dane identyfikacyjne (imię i nazwisko, adres e-mail, numer telefonu)
• Informacje dotyczące kondycji i zdrowia, które zdecydujesz się udostępnić
• Preferencje komunikacyjne
• Wszelkie inne informacje, które zdecydujesz się przekazać`
      }, {
        title: '2. Jak wykorzystujemy Twoje informacje',
        content: `Wykorzystujemy zebrane informacje do:

• Odpowiadania na Twoje zapytania i świadczenia obsługi klienta
• Planowania i zarządzania sesjami treningowymi
• Wysyłania aktualizacji o naszych usługach (za Twoją zgodą)
• Ulepszania naszych usług i rozwijania nowych ofert
• Wypełniania zobowiązań prawnych`
      }, {
        title: '3. Udostępnianie informacji',
        content: `Nie sprzedajemy, nie wymieniamy ani w inny sposób nie przekazujemy Twoich danych osobowych stronom trzecim bez Twojej zgody, z wyjątkiem:

• Zaufanych dostawców usług, którzy pomagają nam w prowadzeniu strony internetowej i działalności
• Gdy jest to wymagane przez prawo lub w celu ochrony naszych praw
• W związku z przeniesieniem działalności lub fuzją`
      }, {
        title: '4. Bezpieczeństwo danych',
        content: `Wdrażamy odpowiednie środki bezpieczeństwa w celu ochrony Twoich danych osobowych przed nieautoryzowanym dostępem, zmianą, ujawnieniem lub zniszczeniem. Jednak żadna metoda transmisji przez Internet nie jest w 100% bezpieczna.`
      }, {
        title: '5. Twoje prawa',
        content: `Masz prawo do:

• Dostępu do danych osobowych, które przechowujemy
• Żądania poprawienia nieprawidłowych informacji
• Żądania usunięcia Twoich danych osobowych
• Rezygnacji z komunikacji marketingowej
• Wycofania zgody w dowolnym momencie`
      }, {
        title: '6. Pliki cookie',
        content: `Nasza strona internetowa może używać plików cookie w celu poprawy komfortu przeglądania. Możesz wyłączyć pliki cookie w ustawieniach przeglądarki, choć może to wpłynąć na niektóre funkcje naszej strony.`
      }, {
        title: '7. Kontakt',
        content: `Jeśli masz pytania dotyczące niniejszej Polityki Prywatności, skontaktuj się z nami:

Email: Example@email.com
Telefon: +48 123 456 789
Adres: Iron District Gym, ul. Fitness 123, Warszawa`
      }]
    }
  };
  const t = content[language];
  return <div className="min-h-screen bg-brand-dark pt-24 pb-16 px-4" data-id="element-133">
      <div className="max-w-4xl mx-auto" data-id="element-134">
        <button onClick={onBack} className="flex items-center gap-2 text-brand-accent hover:text-brand-accentHover transition-colors mb-8 group" data-id="element-135">
          <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" data-id="element-136" />
          <span className="font-bold uppercase tracking-wider text-sm" data-id="element-137">
            {t.backToHome}
          </span>
        </button>

        <h1 className="font-display text-5xl md:text-6xl text-white mb-4" data-id="element-138">
          {t.title}
        </h1>
        <p className="text-gray-500 mb-12" data-id="element-139">{t.lastUpdated}</p>

        <div className="space-y-10" data-id="element-140">
          {t.sections.map((section, index) => <div key={index} className="border-l-2 border-brand-accent/30 pl-6" data-id="element-141">
              <h2 className="font-display text-2xl text-white mb-4" data-id="element-142">
                {section.title}
              </h2>
              <p className="text-gray-400 leading-relaxed whitespace-pre-line" data-id="element-143">
                {section.content}
              </p>
            </div>)}
        </div>
      </div>
    </div>;
}